import {
  inject,
  Injectable,
  Injector,
  runInInjectionContext,
} from '@angular/core';
import { assert, isDefined } from '@sama/js-core';
import { isUndefined } from 'lodash';
import { ObjectState } from '../../../cross-team-shared/annotation/shared/state/object-state';
import {
  getShapeForEventTracking,
  ShapeState,
} from '../../../cross-team-shared/annotation/shared/state/shape-state';
import { MessagingService } from '../../../cross-team-shared/messaging/messaging.service';
import { EventTrackingValueWithSource } from '../../../cross-team-shared/misc-utils';
import { DefaultAttributeService } from '../../../outputs/annotation/annotation-shared/utils/default-attribute.service';
import { TaskWorkspaceStore } from '../../h3ml/state/task-workspace-store';
import { RootPushAndTrackService } from '../../outputs/annotation/annotation-shared/utils/root-push-and-track.service';
import { MultiAnnotationCreationHelper } from './multi-annotation-creation-helper';
import {
  objectSequencesFeaturesErrorCause,
  ObjectService,
} from './object.service';
import { ShapeService } from './shape.service';

@Injectable()
export class AnnotationCreateDeleteService {
  private taskWorkspaceStore = inject(TaskWorkspaceStore);
  private shapeService = inject(ShapeService);
  private objectService = inject(ObjectService);
  private pushAndTrackService = inject(RootPushAndTrackService);
  private defaultAttributeService = inject(DefaultAttributeService);
  private messagingService = inject(MessagingService);
  private injector = inject(Injector);

  createShape(
    shape: ShapeState,
    annotationOutputName: string,
    setDefaultAttributes = true,
    parentObjectId = this.taskWorkspaceStore.state().scopedObjectId,
  ): ShapeState | undefined {
    this.taskWorkspaceStore.batch(() => {
      try {
        if (isDefined(parentObjectId)) {
          shape.parentId = parentObjectId;
        } else {
          shape.parentId = this.objectService.createObject().index;
          this.openClassSelector();
        }
      } catch (error) {
        if ((error as Error).cause === objectSequencesFeaturesErrorCause) {
          this.messagingService.error((error as Error).message);
        }
        return;
      }

      this.shapeService.createShape(shape, annotationOutputName);
      this.taskWorkspaceStore
        .getObjectStore(shape.parentId)('childShapeIds')
        .mutateUsing((shapeIds) => shapeIds.push(shape.index));

      if (setDefaultAttributes) {
        this.refreshNewAttributes(shape);
      }
    });
    return shape;
  }

  createAnnotations(objects: ObjectState[], shapes: ShapeState[]) {
    const helper = runInInjectionContext(
      this.injector,
      () => new MultiAnnotationCreationHelper(objects, shapes),
    );
    const { scopedObjectId } = this.taskWorkspaceStore.state();
    const rootShapes = shapes.filter((shape) => isUndefined(shape.parentId));

    helper.create(
      scopedObjectId,
      objects.filter((object) => isUndefined(object.parentId)),
      rootShapes,
    );
    return { objects: helper.createdObjects, shapes: helper.createdShapes };
  }

  deleteShapes(
    shapeIndices: number[],
    eventTrackingValue?: EventTrackingValueWithSource,
  ) {
    const shapes = this.shapeService.getShapes(shapeIndices);

    this.taskWorkspaceStore.batch(() => {
      const affectedObjectIds = this.getUniqueObjectIdsFromShapes(shapes);

      try {
        this.objectService.removeShapeFromObjects(
          affectedObjectIds,
          shapeIndices,
        );
      } catch (error) {
        if ((error as Error).cause === objectSequencesFeaturesErrorCause) {
          this.messagingService.error((error as Error).message);
        }
        return;
      }

      this.shapeService.deleteShapes(shapeIndices);
    });

    if (eventTrackingValue) {
      this.pushAndTrackService.pushAndTrack({
        id: 'delete_shapes',
        bucket: 'adjustment',
        ...eventTrackingValue,
        shapes: shapes.map(getShapeForEventTracking),
      });
    }
  }

  private getUniqueObjectIdsFromShapes(shapes: ShapeState[]) {
    return Array.from(
      new Set(
        shapes.map((shape) => {
          assert(
            isDefined(shape.parentId),
            'shapes in object enabled projects always have an object id',
          );
          return shape.parentId;
        }),
      ),
    );
  }

  private refreshNewAttributes(shapeState: ShapeState) {
    const shapeStore = this.shapeService.getShapeStore(shapeState.index);

    const objectId = shapeState.parentId;
    assert(isDefined(objectId), 'shape must have a parent object');

    this.defaultAttributeService.refreshAttributes(shapeStore);
  }

  private openClassSelector() {
    this.taskWorkspaceStore('sidePanel')('annotationPanelState').set(
      'class_selector',
    );
  }
}
